/**
 * Where a quotation line will actually be bought from — point 22.
 *
 * The full findings against the live doctypes are in this module's test. In short: `Quotation Line`
 * has `lowest_quote_supplier` (a single Link), `lowest_quote_price`, `selected_rate` and
 * `supplier_note` — and **no RFQ or Supplier Quotation field at all**. So the reference Akhil wants
 * for raising a PO has nowhere proper to live (asked for as `D22`; it is also point 19), and a
 * SPLIT allocation currently records only its cheapest leg, losing the rest entirely.
 *
 * `supplier_note` is free text and is the only field available, so the whole sourcing decision goes
 * there in a form a salesperson can act on.
 *
 * ---- What this is not ----
 *
 * It is not a substitute for real Link fields. Nothing can query it, no automation can read it,
 * and it will not drive a PO by itself. It is the difference between the information being on the
 * document and being lost — nothing more, and it should be replaced the moment the fields exist.
 */

import type { AllocationRow, ComparisonSupplier } from './types'

/**
 * Frappe's `Data` fields hold 140 characters. Beyond that the server truncates mid-word or rejects
 * the write, so the cap is applied here where the tail can be replaced with an honest count.
 */
export const SOURCING_NOTE_MAX = 140

const round2 = (n: number): number => Math.round(n * 100) / 100

/**
 * One line's allocation, as a person reads it:
 *
 *     Al Noor Trading 60 @ 12 (SQ-2026-0007); Gulf Star 40 @ 12.5 (SQ-2026-0009)
 *
 * Display names, not docnames — `lowest_quote_supplier` already holds the Link value, and this is
 * the field a human reads. The SQ reference is omitted rather than written blank when the
 * comparison does not know it: "(…)" with nothing in it reads as a missing value we should have had.
 */
export function buildSourcingNote(
  rows: readonly AllocationRow[],
  suppliers: readonly ComparisonSupplier[],
): string {
  if (rows.length === 0) return ''

  const bySupplier = new Map(suppliers.map((s) => [s.supplier, s]))
  const parts = rows.map((row) => {
    const column = bySupplier.get(row.supplier)
    const name = column?.supplier_name?.trim() || row.supplier
    /*
      The ROW's own quotation first. One supplier may quote the same line several times — a
      revision, a second branch, a re-quote — and the comparison column carries only the first of
      them (`Offer.supplier_quotation` is what tells them apart). Naming the column's would point a
      purchase order at a quotation nobody accepted.
    */
    const sq = row.supplier_quotation?.trim() || column?.sq?.trim()
    return `${name} ${round2(row.allocated_qty)} @ ${round2(row.rate)}${sq ? ` (${sq})` : ''}`
  })

  const full = parts.join('; ')
  if (full.length <= SOURCING_NOTE_MAX) return full

  /*
    Too long for the field. Keep as many whole legs as fit and SAY how many were dropped — a
    silently truncated note reads as the complete sourcing decision, which is the one thing it must
    never do when someone is about to raise purchase orders from it.
  */
  const kept: string[] = []
  let used = 0
  for (const part of parts) {
    // Reserve room for the "; +N more" tail; N is at most the number of legs.
    const tail = `; +${parts.length - kept.length} more`.length
    const cost = part.length + (kept.length > 0 ? 2 : 0)
    if (used + cost + tail > SOURCING_NOTE_MAX) break
    kept.push(part)
    used += cost
  }

  const dropped = parts.length - kept.length
  return `${kept.join('; ')}; +${dropped} more`
}
